import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import AssignmentIcon from "@mui/icons-material/Assignment";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";

import {
  refreshLegalFileById,
  refreshLegalFileByIdFull,
} from "../../../services/LegalFileService";
import { getCustomStatusOptions } from "../../../services/CustomStatusService";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  useOnlineQueryGate,
  ONLINE_QUERIES_OFF_REASON,
} from "../../General/OnlineQueryGate";
import { formatDateTime } from "../../../config/Helper";

import GlassCard from "./GlassCard";
import SectionTitle from "./SectionTitle";
import InfoRow from "./InfoRow";
import LegalFileStageChip from "./LegalFileStageChip";
import LegalFileTypeChip from "./LegalFileTypeChip";
import { getPausedLawyers, LOCKED_LEGAL_FILE_MESSAGE } from "./detailHelpers";
import LawyerSessionRequiredDialog from "../../General/Dialog/LawyerSessionRequiredDialog";

export default function LegalFileHeader({ legalFile, onRefresh }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { online } = useOnlineQueryGate();

  const [refreshing, setRefreshing] = useState(false);
  const [refreshingFull, setRefreshingFull] = useState(false);
  const [statusTaxOptions, setStatusTaxOptions] = useState([]);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const lawsuit = legalFile.lawsuits;
  const pausedLawyers = getPausedLawyers(legalFile);
  const locked = pausedLawyers.length > 0;

  useEffect(() => {
    if (!lawsuit?.status_tax_id) return;
    getCustomStatusOptions("lawsuit_tax")
      .then((res) => setStatusTaxOptions(res.data?.data || []))
      .catch(() => setStatusTaxOptions([]));
  }, [lawsuit?.status_tax_id]);

  const lawsuitStatusName = lawsuit?.status_tax_id
    ? statusTaxOptions.find((option) => option.id === lawsuit.status_tax_id)?.name
    : null;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      showToast("Copiado al portapapeles.", "success");
    } catch {
      showToast("No se pudo copiar.", "error");
    }
  };

  const handleRefreshLegalFile = async () => {
    if (!legalFile?.id) return;

    setRefreshing(true);
    try {
      const res = await refreshLegalFileById(legalFile.id);

      if (res.data?.status === "success") {
        const newOps = res.data?.data?.new_operations ?? 0;

        showToast(
          newOps > 0
            ? `Expediente actualizado. Nuevas operaciones: ${newOps}`
            : "Expediente actualizado. Sin cambios.",
          "success"
        );

        await onRefresh();
      } else {
        showToast(
          res.data?.message || "No se pudo actualizar el expediente.",
          "error"
        );
      }
    } catch (err) {
      if (err.response?.data?.code === "judicial_session_required") {
        setPendingAction("refresh");
        setSessionRequiredLawyer(err.response.data.lawyer || {});
      } else {
        const msg =
          err.response?.data?.message ||
          (err.response?.status === 401
            ? "No se pudo iniciar sesión con el abogado."
            : "Error al actualizar el expediente.");
        showToast(msg, "error");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefreshLegalFileFull = async () => {
    if (!legalFile?.id) return;
    setRefreshingFull(true);
    try {
      const res = await refreshLegalFileByIdFull(legalFile.id);
      if (res.data?.status === "success") {
        const newOps = res.data?.data?.new_operations ?? 0;
        showToast(
          newOps > 0
            ? `Actualización completa finalizada. ${newOps} operación(es) nueva(s) importada(s).`
            : "Actualización completa finalizada. No se encontraron operaciones nuevas.",
          "success"
        );
        await onRefresh();
      } else {
        showToast(
          res.data?.message || "No se pudo realizar la actualización completa.",
          "error"
        );
      }
    } catch (err) {
      if (err.response?.data?.code === "judicial_session_required") {
        setPendingAction("full");
        setSessionRequiredLawyer(err.response.data.lawyer || {});
      } else {
        showToast(
          err.response?.data?.message || "Error en la actualización completa.",
          "error"
        );
      }
    } finally {
      setRefreshingFull(false);
    }
  };

  return (
    <>
      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => {
          setSessionRequiredLawyer(null);
          setPendingAction(null);
        }}
        lawyer={sessionRequiredLawyer}
        onCaptured={() => {
          const action = pendingAction;
          setPendingAction(null);
          if (action === "full") {
            handleRefreshLegalFileFull();
          } else if (action === "refresh") {
            handleRefreshLegalFile();
          }
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
      <GlassCard
        sx={{
          py: 1.1,
          px: 1.4,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <LegalFileStageChip legalFile={legalFile} onRefresh={onRefresh} />

        <LegalFileTypeChip legalFile={legalFile} onRefresh={onRefresh} />

        {lawsuitStatusName && (
          <Tooltip title="Estado del sistema (título)">
            <Chip
              label={lawsuitStatusName}
              size="medium"
              sx={(theme) => ({
                fontWeight: 800,
                borderRadius: 2,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "grey.300",
                color: "primary.main",
              })}
            />
          </Tooltip>
        )}

        {locked && (
          <Tooltip
            title={`${LOCKED_LEGAL_FILE_MESSAGE} (${pausedLawyers
              .map((l) => `${l.name} ${l.lastname}`)
              .join(", ")})`}
          >
            <Chip
              icon={<PauseCircleOutlineIcon />}
              label="Abogado pausado"
              size="medium"
              color="warning"
              sx={{ fontWeight: 800, borderRadius: 2 }}
            />
          </Tooltip>
        )}

        <Tooltip
          title={
            !online
              ? ONLINE_QUERIES_OFF_REASON
              : locked
                ? LOCKED_LEGAL_FILE_MESSAGE
                : "Actualizar expediente"
          }
        >
          <span>
            <IconButton
              onClick={handleRefreshLegalFile}
              disabled={
                refreshing ||
                refreshingFull ||
                locked ||
                !online ||
                Boolean(sessionRequiredLawyer) ||
                !hasPermission("actualizar expediente")
              }
              sx={{
                borderRadius: 2,
                bgcolor: "rgba(0,0,0,0.04)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
              }}
            >
              <AutorenewRoundedIcon
                fontSize="small"
                sx={{
                  ...(refreshing && {
                    animation: "spin 1s linear infinite",
                  }),
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {hasPermission("actualizar expediente completo") && (
          <Tooltip
            title={
              !online
                ? ONLINE_QUERIES_OFF_REASON
                : locked
                  ? LOCKED_LEGAL_FILE_MESSAGE
                  : "Actualización completa — importa todas las operaciones históricas"
            }
          >
            <span>
              <IconButton
                onClick={handleRefreshLegalFileFull}
                disabled={
                  refreshingFull ||
                  refreshing ||
                  locked ||
                  !online ||
                  Boolean(sessionRequiredLawyer)
                }
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(234,179,8,0.10)",
                  color: "warning.main",
                  "&:hover": { bgcolor: "rgba(234,179,8,0.18)" },
                }}
              >
                <SyncAltIcon
                  fontSize="small"
                  sx={{
                    ...(refreshingFull && {
                      animation: "syncFlip 0.9s ease-in-out infinite",
                      "@keyframes syncFlip": {
                        "0%":   { transform: "rotateY(0deg)" },
                        "50%":  { transform: "rotateY(180deg)" },
                        "100%": { transform: "rotateY(360deg)" },
                      },
                    }),
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Copiar código">
          <IconButton
            onClick={() => copyToClipboard(legalFile.code)}
            sx={{
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.04)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
            }}
          >
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </GlassCard>
      </Box>

      <GlassCard>
      <SectionTitle
        icon={<AssignmentIcon />}
        title={`Expediente ${legalFile.code}`}
        description={legalFile.name || ""}
      />

      <Divider sx={{ opacity: 0.6, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow label="Dependencia" value={legalFile.dependencies} />
          <InfoRow label="Juzgado" value={legalFile.type_judge} />
          <InfoRow label="Ubicación" value={legalFile.location} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow label="Estado" value={legalFile.status} />
          <InfoRow
            label="Fecha de inicio"
            value={
              legalFile.start_date
                ? new Date(legalFile.start_date).toLocaleDateString(
                    "es-AR",
                    { day: "2-digit", month: "2-digit", year: "numeric" },
                  )
                : "—"
            }
          />

          <InfoRow
            label="Fecha de Actualización"
            value={
              legalFile.updated_at
                ? new Date(legalFile.updated_at).toLocaleDateString(
                    "es-AR",
                    { day: "2-digit", month: "2-digit", year: "numeric" },
                  )
                : "—"
            }
          />

          {hasPermission("ver cuentas judiciales") && (
            <>
              <InfoRow
                label="Saldo actual (cuenta judicial)"
                value={
                  legalFile.judicial_balance_current != null &&
                  legalFile.judicial_balance_current !== ""
                    ? new Intl.NumberFormat("es-AR", {
                        style: "currency",
                        currency: "ARS",
                        minimumFractionDigits: 2,
                      }).format(
                        Number(legalFile.judicial_balance_current),
                      )
                    : "—"
                }
              />

              <InfoRow
                label="Fecha saldo"
                value={
                  legalFile.judicial_balance_current_at
                    ? formatDateTime(
                        legalFile.judicial_balance_current_at,
                      )
                    : "—"
                }
              />
            </>
          )}

          <Box sx={{ mt: 0.6 }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Abogados
            </Typography>
            <Box
              sx={{ mt: 0.8, display: "flex", gap: 1, flexWrap: "wrap" }}
            >
              {legalFile.lawyers?.length ? (
                legalFile.lawyers.map((l) => (
                  <Chip
                    key={l.id}
                    icon={l.updates_paused ? <PauseCircleOutlineIcon /> : undefined}
                    label={`${l.name} ${l.lastname}${l.updates_paused ? " (pausado)" : ""}`}
                    size="small"
                    sx={
                      l.updates_paused
                        ? {
                            fontWeight: 800,
                            bgcolor: "rgba(237,108,2,0.12)",
                            color: "warning.main",
                            maxWidth: "100%",
                          }
                        : {
                            fontWeight: 800,
                            bgcolor: "rgba(25,118,210,0.10)",
                            color: "primary.main",
                            maxWidth: "100%",
                          }
                    }
                  />
                ))
              ) : (
                <Chip
                  label="Sin asignar"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      </GlassCard>
    </>
  );
}
